import { create } from "zustand"
import { fetchConsolePrefs, pushConsolePrefs } from "@/core/user-prefs"

// Small persisted UI-preference store for the chrome. Initialized to defaults
// (so SSR + first client render match), then hydrated from localStorage on mount
// via hydrateShellPrefs() — same no-mismatch approach as the theme provider.
//
// The three VIEW-MENU prefs below (heroCompact, recordsFullscreen, enterToSend)
// are also synced to the user's GLOBAL account prefs (server-side), so they
// follow the user across browsers/devices. localStorage stays as a fast,
// offline cache for instant first paint; the server value (loaded in
// hydrateShellPrefs) wins when present. Theme is the same concern but lives in
// the theme provider (lib/theme.tsx). Per-section `collapsed`/`starred` stay
// browser-local — they're ephemeral, high-churn, and not "settings".
const HERO_KEY = "attend-console-hero-compact"
const RECORDS_FS_KEY = "attend-console-records-fullscreen"
const ENTER_SEND_KEY = "attend-console-enter-to-send"
const COLLAPSE_PREFIX = "attend-console-rail-collapsed:"
const STAR_PREFIX = "attend-console-channel-star:"
const TRUNCATE_PREFIX = "attend-console-table-truncate:"
const COLWIDTH_PREFIX = "attend-console-table-colwidth:"
const COLHIDE_PREFIX = "attend-console-table-colhide:"
const COLORDER_PREFIX = "attend-console-table-colorder:"

type ShellPrefs = {
  heroCompact: boolean
  setHeroCompact: (v: boolean) => void
  // Message composer: when true (default, Slack-style), Enter SENDS and
  // Shift+Enter inserts a newline. When false, Enter inserts a newline and
  // Cmd/Ctrl+Enter sends. Read live by the composers' keydown handler.
  enterToSend: boolean
  setEnterToSend: (v: boolean) => void
  // "Open records full-width": when true, records (a guest, a travel segment, a
  // session, a form…) open FULL-WIDTH instead of beside the list in a split
  // column. ColumnLayout reads this; the toggle lives in the top-bar View menu.
  recordsFullscreen: boolean
  setRecordsFullscreen: (v: boolean) => void
  // Per-section collapsed state for the left-rail accordions (channels, dms,
  // manage, …). Defaults to expanded (false) so SSR + first client render match
  // before hydrateShellPrefs() applies the stored choice.
  collapsed: Record<string, boolean>
  toggleCollapsed: (section: string) => void
  // Per-USER starred channels (this browser) — channel id → true. Surfaces a
  // "Starred" section at the top of the rail's channel area. Distinct from the
  // shared `pinnedAtTop` (which pins a channel for everyone).
  starred: Record<string, boolean>
  toggleStar: (channelId: string) => void
  // Table truncation (View menu + per-column header menus). Collection-level
  // keys are `${title}:headers` / `:cells`; per-column OVERRIDES are
  // `${title}:col:${colId}:headers` / `:cells` (absent = inherit the
  // collection default). Browser-local like collapsed.
  tableTruncate: Record<string, boolean>
  /** Explicit set (stores "0" so an explicit OFF is distinct from "unset" —
      needed both for per-column inherit and for default-ON collection keys). */
  setTableTruncate: (key: string, v: boolean) => void
  // Manual column widths (px) from the header drag handles — key is
  // `${title}:${colId}`; null clears back to automatic sizing.
  tableColWidth: Record<string, number>
  setTableColWidth: (collection: string, colId: string, px: number | null) => void
  // Hidden columns — key `${title}:${colId}` → true. Restored via the View
  // menu's Columns section.
  tableHidden: Record<string, boolean>
  setTableHidden: (collection: string, colId: string, hidden: boolean) => void
  // Column display order per collection (visible-column id list from the
  // header menus' Move left/right); ids not listed keep their spec order.
  tableOrder: Record<string, string[]>
  setTableOrder: (collection: string, ids: string[]) => void
}

export const useShellPrefs = create<ShellPrefs>((set, get) => ({
  heroCompact: false,
  setHeroCompact: (v) => {
    try {
      localStorage.setItem(HERO_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ heroCompact: v })
    pushConsolePrefs({ heroCompact: v })
  },
  recordsFullscreen: false,
  setRecordsFullscreen: (v) => {
    try {
      localStorage.setItem(RECORDS_FS_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ recordsFullscreen: v })
    pushConsolePrefs({ recordsFullscreen: v })
  },
  enterToSend: true,
  setEnterToSend: (v) => {
    try {
      localStorage.setItem(ENTER_SEND_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ enterToSend: v })
    pushConsolePrefs({ enterToSend: v })
  },
  collapsed: {},
  toggleCollapsed: (section) => {
    const next = !get().collapsed[section]
    try {
      localStorage.setItem(COLLAPSE_PREFIX + section, next ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ collapsed: { ...s.collapsed, [section]: next } }))
  },
  starred: {},
  toggleStar: (channelId) => {
    const next = !get().starred[channelId]
    try {
      if (next) localStorage.setItem(STAR_PREFIX + channelId, "1")
      else localStorage.removeItem(STAR_PREFIX + channelId)
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ starred: { ...s.starred, [channelId]: next } }))
  },
  tableTruncate: {},
  setTableTruncate: (key, v) => {
    try {
      localStorage.setItem(TRUNCATE_PREFIX + key, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ tableTruncate: { ...s.tableTruncate, [key]: v } }))
  },
  tableColWidth: {},
  setTableColWidth: (collection, colId, pxWidth) => {
    const key = `${collection}:${colId}`
    try {
      if (pxWidth != null) localStorage.setItem(COLWIDTH_PREFIX + key, String(pxWidth))
      else localStorage.removeItem(COLWIDTH_PREFIX + key)
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => {
      const next = { ...s.tableColWidth }
      if (pxWidth != null) next[key] = pxWidth
      else delete next[key]
      return { tableColWidth: next }
    })
  },
  tableHidden: {},
  setTableHidden: (collection, colId, hidden) => {
    const key = `${collection}:${colId}`
    try {
      if (hidden) localStorage.setItem(COLHIDE_PREFIX + key, "1")
      else localStorage.removeItem(COLHIDE_PREFIX + key)
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ tableHidden: { ...s.tableHidden, [key]: hidden } }))
  },
  tableOrder: {},
  setTableOrder: (collection, ids) => {
    try {
      localStorage.setItem(COLORDER_PREFIX + collection, JSON.stringify(ids))
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ tableOrder: { ...s.tableOrder, [collection]: ids } }))
  },
}))

// Read persisted prefs into the store. Call once on the client after mount.
export function hydrateShellPrefs() {
  if (typeof window === "undefined") return
  try {
    const collapsed: Record<string, boolean> = {}
    const starred: Record<string, boolean> = {}
    const tableTruncate: Record<string, boolean> = {}
    const tableColWidth: Record<string, number> = {}
    const tableHidden: Record<string, boolean> = {}
    const tableOrder: Record<string, string[]> = {}
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(COLLAPSE_PREFIX)) {
        collapsed[key.slice(COLLAPSE_PREFIX.length)] = localStorage.getItem(key) === "1"
      } else if (key?.startsWith(STAR_PREFIX)) {
        starred[key.slice(STAR_PREFIX.length)] = localStorage.getItem(key) === "1"
      } else if (key?.startsWith(TRUNCATE_PREFIX)) {
        tableTruncate[key.slice(TRUNCATE_PREFIX.length)] = localStorage.getItem(key) === "1"
      } else if (key?.startsWith(COLWIDTH_PREFIX)) {
        const n = Number(localStorage.getItem(key))
        if (Number.isFinite(n) && n > 0) tableColWidth[key.slice(COLWIDTH_PREFIX.length)] = n
      } else if (key?.startsWith(COLHIDE_PREFIX)) {
        tableHidden[key.slice(COLHIDE_PREFIX.length)] = localStorage.getItem(key) === "1"
      } else if (key?.startsWith(COLORDER_PREFIX)) {
        try {
          const ids = JSON.parse(localStorage.getItem(key) ?? "")
          if (Array.isArray(ids)) tableOrder[key.slice(COLORDER_PREFIX.length)] = ids
        } catch {
          // ignore malformed entries
        }
      }
    }
    useShellPrefs.setState({
      heroCompact: localStorage.getItem(HERO_KEY) === "1",
      recordsFullscreen: localStorage.getItem(RECORDS_FS_KEY) === "1",
      // Default ON unless explicitly disabled.
      enterToSend: localStorage.getItem(ENTER_SEND_KEY) !== "0",
      collapsed,
      starred,
      tableTruncate,
      tableColWidth,
      tableHidden,
      tableOrder,
    })
  } catch {
    // ignore
  }

  // Then overlay the account-synced values (server is the global source of
  // truth for these three; localStorage above was just the instant-paint
  // cache). Best-effort: fetchConsolePrefs never throws — if the user is
  // signed out or the endpoint isn't deployed yet, this is a no-op and the
  // local values stand. Each applied value is written back to localStorage so
  // the next cold start paints the synced choice immediately.
  void fetchConsolePrefs().then((p) => {
    const patch: { heroCompact?: boolean; recordsFullscreen?: boolean; enterToSend?: boolean } = {}
    if (typeof p.heroCompact === "boolean") {
      patch.heroCompact = p.heroCompact
      try {
        localStorage.setItem(HERO_KEY, p.heroCompact ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (typeof p.recordsFullscreen === "boolean") {
      patch.recordsFullscreen = p.recordsFullscreen
      try {
        localStorage.setItem(RECORDS_FS_KEY, p.recordsFullscreen ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (typeof p.enterToSend === "boolean") {
      patch.enterToSend = p.enterToSend
      try {
        localStorage.setItem(ENTER_SEND_KEY, p.enterToSend ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (Object.keys(patch).length) useShellPrefs.setState(patch)
  })
}
